/**
 * Feature Flags Configuration
 * 
 * Centralized feature flag management for A2A integration.
 * Flags are loaded from environment variables.
 */

/**
 * Feature Flags
 * 
 * These flags control the behavior of the application:
 * - USE_A2A_BACKEND: Enable/disable A2A backend
 * - FALLBACK_TO_SUPABASE: Enable/disable Supabase fallback
 */
export const FeatureFlags = {
  /**
   * Enable A2A backend
   * Default: true (enabled by default)
   * Set to 'false' in env to use Supabase exclusively
   */
  USE_A2A_BACKEND: process.env.REACT_APP_USE_A2A_BACKEND !== 'false',
  
  /**
   * Enable Supabase fallback
   * Default: true (enabled by default)
   * If A2A fails and this is true, fallback to Supabase
   * If false, throw errors when A2A fails
   */
  FALLBACK_TO_SUPABASE: process.env.REACT_APP_FALLBACK_TO_SUPABASE !== 'false',
  
  /**
   * A2A Backend URL
   * URL of the A2A backend server
   */
  A2A_BACKEND_URL: process.env.REACT_APP_A2A_BACKEND_URL || 'https://a2aenglishai-server.ymanc96868126.workers.dev',
  
  /**
   * A2A Secret Key
   * Secret key for message signing (optional)
   */
  A2A_SECRET_KEY: process.env.REACT_APP_A2A_SECRET_KEY || null,
  
  /**
   * DeepSeek API Key
   * API key for DeepSeek AI features (if client-side is needed)
   */
  DEEPSEEK_API_KEY: process.env.REACT_APP_DEEPSEEK_API_KEY || null,
  
  /**
   * Enable Logging
   * Automatically enabled in development mode
   */
  ENABLE_LOGGING: process.env.NODE_ENV === 'development',
};

/**
 * Feature Flag Status
 * Returns a human-readable status of feature flags
 */
export const getFeatureFlagStatus = () => {
  return {
    'A2A Backend': FeatureFlags.USE_A2A_BACKEND ? '✅ Enabled' : '❌ Disabled',
    'Supabase Fallback': FeatureFlags.FALLBACK_TO_SUPABASE ? '✅ Enabled' : '❌ Disabled',
    'Backend URL': FeatureFlags.A2A_BACKEND_URL,
    'Has Secret Key': FeatureFlags.A2A_SECRET_KEY ? '✅ Yes' : '❌ No',
    'Logging': FeatureFlags.ENABLE_LOGGING ? '✅ Enabled' : '❌ Disabled',
    'Environment': process.env.NODE_ENV,
  };
};

/**
 * Log feature flag status (development only)
 */
if (FeatureFlags.ENABLE_LOGGING) {
  console.log('=== Feature Flags ===');
  const status = getFeatureFlagStatus();
  Object.entries(status).forEach(([key, value]) => {
    console.log(`${key}: ${value}`);
  });
  console.log('====================');
}

/**
 * Feature Flag Descriptions
 * Helpful descriptions for each flag
 */
export const FeatureFlagDescriptions = {
  USE_A2A_BACKEND: {
    name: 'Use A2A Backend',
    description: 'Enable A2A backend for all data operations',
    environmentVariable: 'REACT_APP_USE_A2A_BACKEND',
    defaultValue: 'true',
    values: {
      true: 'Use A2A backend with optional Supabase fallback',
      false: 'Use Supabase exclusively'
    }
  },
  FALLBACK_TO_SUPABASE: {
    name: 'Fallback to Supabase',
    description: 'Fallback to Supabase when A2A operations fail',
    environmentVariable: 'REACT_APP_FALLBACK_TO_SUPABASE',
    defaultValue: 'true',
    values: {
      true: 'Fallback to Supabase on A2A failure (safe for migration)',
      false: 'Throw errors on A2A failure (production-ready mode)'
    }
  },
  A2A_BACKEND_URL: {
    name: 'A2A Backend URL',
    description: 'URL of the A2A backend server',
    environmentVariable: 'REACT_APP_A2A_BACKEND_URL',
    defaultValue: 'https://a2aenglishai-server.ymanc96868126.workers.dev',
    examples: [
      'http://localhost:8787 (local development)',
      'https://a2aenglishai-server.ymanc96868126.workers.dev (production)'
    ]
  },
  A2A_SECRET_KEY: {
    name: 'A2A Secret Key',
    description: 'Secret key for HMAC message signing (optional)',
    environmentVariable: 'REACT_APP_A2A_SECRET_KEY',
    defaultValue: 'null',
    note: 'Optional - only needed for enhanced security'
  }
};

/**
 * Validate Feature Flags
 * Checks if feature flags are properly configured
 */
export const validateFeatureFlags = () => {
  const errors = [];
  const warnings = [];

  // Check if A2A backend URL is set
  if (FeatureFlags.USE_A2A_BACKEND && !FeatureFlags.A2A_BACKEND_URL) {
    errors.push('A2A backend is enabled but REACT_APP_A2A_BACKEND_URL is not set');
  }

  // Check for conflicting settings
  if (!FeatureFlags.USE_A2A_BACKEND && !FeatureFlags.FALLBACK_TO_SUPABASE) {
    errors.push('Both A2A and Supabase are disabled - no backend available');
  }

  // Warn if fallback is disabled in development
  if (FeatureFlags.USE_A2A_BACKEND && 
      !FeatureFlags.FALLBACK_TO_SUPABASE && 
      process.env.NODE_ENV === 'development') {
    warnings.push('Supabase fallback is disabled in development - this may cause errors');
  }

  // Warn if secret key is not set in production
  if (FeatureFlags.USE_A2A_BACKEND && 
      !FeatureFlags.A2A_SECRET_KEY && 
      process.env.NODE_ENV === 'production') {
    warnings.push('A2A secret key is not set in production - message signing disabled');
  }

  return {
    valid: errors.length === 0,
    errors,
    warnings
  };
};

/**
 * Run validation in development mode
 */
if (FeatureFlags.ENABLE_LOGGING) {
  const validation = validateFeatureFlags();
  
  if (validation.errors.length > 0) {
    console.error('❌ Feature Flag Errors:');
    validation.errors.forEach(error => console.error(`  - ${error}`));
  }
  
  if (validation.warnings.length > 0) {
    console.warn('⚠️ Feature Flag Warnings:');
    validation.warnings.forEach(warning => console.warn(`  - ${warning}`));
  }
  
  if (validation.valid && validation.warnings.length === 0) {
    console.log('✅ Feature flags are properly configured');
  }
}

/**
 * Export default
 */
export default FeatureFlags;

